import {useState, useMemo, type ReactNode} from 'react';
import {Search, X, ChevronRight, Check, CheckCircle2, AlertTriangle, Sparkles} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Field,
  FieldDescription,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {type ConfigItem} from '@/api/config';
import {useLanguageSearch} from '@/api/hooks';
import {
  buildLocationPaths,
  buildLocationTree,
  ChevronWidth,
  indentFor,
  LabelPadding
} from '@/components/classify/locations';
import {PLATFORM_ID} from '@/lib/platform';
import {ExamplePinPicker} from '@/components/classify/ExamplePinPicker';
import {ExampleVideoPicker} from '@/components/classify/ExampleVideoPicker';
import {cn} from '@/lib/utils';
import {NoteScreenshots} from '@/components/classify/NoteScreenshots';

export type EnrichmentValues = {
  platform: string;
  submissionType: 'Actor' | 'Content';
  notes: string;
  // SHA-256 of screenshots attached to the notes; internal QA evidence, never delivered.
  attachments?: string[];
  // Actor-only: example videos demonstrating the channel's violation. Stored as a raw string (one id
  // per line) carried straight through to delivery; ExampleVideoPicker reads and writes it that way.
  exampleVideoIds: string;
  languages: string[];
  topics: string[];
  locations: string[];
  coreRisks: string[];
};

/// One Core Risk picker: the vertical it belongs to, and the only resolver risks a submission under
/// it may carry (the union of that vertical's selected leaves' mappings).
export type CoreRiskField = {
  readonly label: string;
  readonly options: string[];
};

type Props = {
  // One entry per selected vertical. Empty when no selected risk maps to any resolver risk, which
  // hides the Core Risk section entirely.
  readonly coreRiskFields: readonly CoreRiskField[];
  readonly values: EnrichmentValues;
  readonly onChange: (values: EnrichmentValues) => void;
  readonly languages: ConfigItem[];
  readonly topics: ConfigItem[];
  readonly locations: ConfigItem[];
  // Name of the language auto-selected from the item's defaultLanguage/defaultAudioLanguage
  // metadata, if any — drives the "we picked this for you" banner above the picker.
  readonly autoSelectedLanguage?: string | null;
  // Channel being classified. When set, the Example Video IDs field offers its latest uploads;
  // without it (batch, or a caller that doesn't know the channel) it stays a plain textarea.
  readonly channelId?: string | null | undefined;
  // Pinterest handle of the profile being classified; Falcon's counterpart of channelId, offering the
  // profile's latest pins as Example Pins.
  readonly actorHandle?: string | null | undefined;
  // A batch picks example ids per channel in the Confirm step's overrides, so the shared field is hidden.
  readonly hideExampleIds?: boolean;
  // A screenshot-discouraged vertical (see NoteScreenshots) is selected.
  readonly discourageScreenshots?: boolean;
  // Name of the location auto-selected from the item's country metadata, if any — same banner
  // treatment as autoSelectedLanguage.
  readonly autoSelectedLocation?: string | null;
  // Raw defaultAudioLanguage/defaultLanguage/country values behind the two auto-selects above,
  // shown next to the relevant picker so an analyst can weigh the source metadata, not just the
  // auto-pick.
  readonly metadataSignals?: {
    defaultAudioLanguage: string | null;
    defaultLanguage: string | null;
    country: string | null;
  };
  // Set only when classifying a batch of 2+ items — auto-select only ever looks at one item's
  // metadata, so analysts need this spelled out per field: whether the pick actually applies to
  // every item, or whether some items disagreed and got their own value instead.
  readonly batchAutoSelectInfo?:
    | {
        totalItems: number;
        languageDeviationCount: number;
        locationDeviationCount: number;
      }
    | undefined;
};

export function EnrichmentForm({
  coreRiskFields,
  values,
  onChange,
  languages,
  topics,
  locations,
  autoSelectedLanguage,
  channelId,
  actorHandle,
  hideExampleIds,
  discourageScreenshots,
  autoSelectedLocation,
  metadataSignals,
  batchAutoSelectInfo
}: Props) {
  const update = <K extends keyof EnrichmentValues>(key: K, value: EnrichmentValues[K]) => {
    onChange({...values, [key]: value});
  };

  const [locationSearch, setLocationSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');
  const [coreRiskSearch, setCoreRiskSearch] = useState('');

  const filteredTopics = topicSearch
    ? topics.filter((t) => t.name.toLowerCase().includes(topicSearch.toLowerCase()))
    : topics;

  const toggleValue = (list: string[], name: string) =>
    list.includes(name) ? list.filter((x) => x !== name) : [...list, name];

  return (
    <div className="space-y-5">
      {languages.length > 0 ? (
        <FormField label="Language">
          {autoSelectedLanguage && values.languages.includes(autoSelectedLanguage) ? (
            <Alert>
              <Sparkles />
              <AlertDescription className="space-y-1.5">
                <p>
                  We&apos;ve auto-selected <strong>{autoSelectedLanguage}</strong> based on metadata
                  from Pioneer — you can change this.
                </p>
                {batchAutoSelectInfo ? (
                  <BatchAutoSelectNote
                    totalItems={batchAutoSelectInfo.totalItems}
                    deviationCount={batchAutoSelectInfo.languageDeviationCount}
                    fieldLabel="language"
                  />
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {metadataSignals?.defaultAudioLanguage || metadataSignals?.defaultLanguage ? (
            <FieldDescription>
              From metadata: Default Audio Language{' '}
              <strong>{metadataSignals.defaultAudioLanguage ?? '—'}</strong> · Default Language{' '}
              <strong>{metadataSignals.defaultLanguage ?? '—'}</strong>
            </FieldDescription>
          ) : null}
          <LanguagePicker
            languages={languages}
            selected={values.languages}
            onToggle={(name) => {
              update('languages', toggleValue(values.languages, name));
            }}
          />
        </FormField>
      ) : null}

      {locations.length > 0 ? (
        <FormField label="Location">
          {autoSelectedLocation && values.locations.includes(autoSelectedLocation) ? (
            <Alert>
              <Sparkles />
              <AlertDescription className="space-y-1.5">
                <p>
                  We&apos;ve auto-selected <strong>{autoSelectedLocation}</strong> based on metadata
                  from Pioneer — you can change this.
                </p>
                {batchAutoSelectInfo ? (
                  <BatchAutoSelectNote
                    totalItems={batchAutoSelectInfo.totalItems}
                    deviationCount={batchAutoSelectInfo.locationDeviationCount}
                    fieldLabel="location"
                  />
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {metadataSignals?.country ? (
            <FieldDescription>
              From metadata: Country <strong>{metadataSignals.country}</strong>
            </FieldDescription>
          ) : null}
          <LocationPicker
            locations={locations}
            selected={values.locations}
            search={locationSearch}
            onToggle={(name) => {
              update('locations', toggleValue(values.locations, name));
            }}
            onSearch={setLocationSearch}
          />
        </FormField>
      ) : null}

      {topics.length > 0 ? (
        <FormField label="Topics">
          <MultiPicker
            items={filteredTopics}
            selected={values.topics}
            search={topicSearch}
            placeholder="Search topics…"
            onToggle={(name) => {
              update('topics', toggleValue(values.topics, name));
            }}
            onSearch={setTopicSearch}
          />
        </FormField>
      ) : null}

      {coreRiskFields.map((field) => {
        // Only the resolver risks this vertical's selected risks map to - never the whole list.
        const items = field.options.map((name) => ({id: name, name}));
        const onlyCoreRisk = items.length === 1 ? items[0]?.name : undefined;
        const filtered = coreRiskSearch
          ? items.filter((cr) => cr.name.toLowerCase().includes(coreRiskSearch.toLowerCase()))
          : items;

        return (
          <FormField
            key={field.label}
            label={coreRiskFields.length > 1 ? `Core Risk — ${field.label}` : 'Core Risk'}>
            {onlyCoreRisk && values.coreRisks.includes(onlyCoreRisk) ? (
              <Alert>
                <Sparkles />
                <AlertDescription>
                  <p>
                    We&apos;ve auto-selected <strong>{onlyCoreRisk}</strong>, the only resolver risk{' '}
                    <strong>{field.label}</strong> maps to.
                  </p>
                </AlertDescription>
              </Alert>
            ) : null}
            {items.length > 1 && !field.options.some((o) => values.coreRisks.includes(o)) ? (
              <Alert>
                <AlertTriangle />
                <AlertDescription>
                  <p>
                    <strong>{field.label}</strong> maps to {items.length} resolver risks — select
                    the one this falls under.
                  </p>
                </AlertDescription>
              </Alert>
            ) : null}
            <MultiPicker
              items={filtered}
              selected={values.coreRisks}
              search={coreRiskSearch}
              placeholder="Search core risks…"
              onToggle={(name) => {
                update('coreRisks', toggleValue(values.coreRisks, name));
              }}
              onSearch={setCoreRiskSearch}
            />
          </FormField>
        );
      })}

      <FormField label="Notes">
        <NoteScreenshots
          discouraged={discourageScreenshots ?? false}
          shas={values.attachments ?? []}
          onChange={(next) => {
            update('attachments', next);
          }}>
          <Textarea
            placeholder="Context, observations, URL list…"
            value={values.notes}
            rows={3}
            className="resize-none"
            onChange={(event) => {
              update('notes', event.target.value);
            }}
          />
        </NoteScreenshots>
      </FormField>

      {values.submissionType === 'Actor' && !hideExampleIds ? (
        <FormField label={exampleIdsLabel}>
          <ExampleIdsInput
            channelId={channelId}
            actorHandle={actorHandle}
            value={values.exampleVideoIds}
            onChange={(next) => {
              update('exampleVideoIds', next);
            }}
          />
        </FormField>
      ) : null}
    </div>
  );
}

export const exampleIdsLabel = PLATFORM_ID === 'pinterest' ? 'Example Pins' : 'Example Video IDs';

/** Example videos/pins for one actor: its latest uploads to pick from when the channel/profile is known, free text otherwise. */
export function ExampleIdsInput({
  channelId,
  actorHandle,
  value,
  onChange
}: {
  readonly channelId?: string | null | undefined;
  readonly actorHandle?: string | null | undefined;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return PLATFORM_ID === 'pinterest' && actorHandle ? (
    <ExamplePinPicker username={actorHandle} value={value} onChange={onChange} />
  ) : channelId && PLATFORM_ID !== 'pinterest' ? (
    <ExampleVideoPicker channelId={channelId} value={value} onChange={onChange} />
  ) : (
    <Textarea
      placeholder={
        PLATFORM_ID === 'pinterest'
          ? "Pin links or IDs demonstrating this profile's violation, one per line — optional"
          : "Video IDs demonstrating this channel's violation, one per line — optional"
      }
      value={value}
      rows={2}
      className="resize-none"
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}

function FormField({label, children}: {readonly label: ReactNode; readonly children: ReactNode}) {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </Field>
  );
}

// Auto-select only ever looks at one item's metadata, so a batch banner saying just "we've
// auto-selected X" reads as if it applies to every item — it doesn't always. This spells out
// whether it actually does, and if not, how many items aren't confirmed to match (whether they
// disagree outright or simply have no metadata to check) and where to fix it.
function BatchAutoSelectNote({
  totalItems,
  deviationCount,
  fieldLabel
}: {
  readonly totalItems: number;
  readonly deviationCount: number;
  readonly fieldLabel: string;
}) {
  if (deviationCount === 0) {
    return (
      <p className="text-foreground flex items-center gap-1 font-medium">
        <CheckCircle2 className="size-3 shrink-0" />
        Matches all {totalItems} items in this batch.
      </p>
    );
  }

  return (
    <p className="text-foreground flex items-start gap-1.5 font-medium">
      <AlertTriangle className="mt-0.5 size-3 shrink-0" />
      <span>
        {deviationCount} of {totalItems} items {deviationCount === 1 ? "isn't" : "aren't"} confirmed
        to match this {fieldLabel} — some have their own value already, others just don&apos;t have
        the metadata to check. Review <strong>&quot;Item overrides&quot;</strong> on the Confirm
        step before submitting.
      </span>
    </p>
  );
}

// The selected-values strip and search row every picker in this form shares: MultiPicker (topics,
// core risks, languages) and LocationPicker draw the same chrome so the four read as one control.
function SelectedChips({
  selected,
  onToggle
}: {
  readonly selected: string[];
  readonly onToggle: (name: string) => void;
}) {
  if (selected.length === 0) {
    return null;
  }

  return (
    <div className="border-input bg-muted/30 flex flex-wrap gap-1.5 border-b p-2">
      {selected.map((name) => (
        <Badge key={name} variant="secondary">
          {name}
          <button
            type="button"
            aria-label={`Remove ${name}`}
            className="hover:text-foreground -mr-0.5 inline-flex"
            onClick={() => {
              onToggle(name);
            }}>
            <X className="size-2.5" />
          </button>
        </Badge>
      ))}
    </div>
  );
}

function PickerSearch({
  value,
  placeholder,
  onChange
}: {
  readonly value: string;
  readonly placeholder: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <InputGroup className="border-input border-0 border-b">
      <InputGroupAddon>
        <Search />
      </InputGroupAddon>
      <InputGroupInput
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </InputGroup>
  );
}

export function MultiPicker({
  items,
  selected,
  onToggle,
  search,
  onSearch,
  placeholder,
  emptyLabel = 'No results'
}: {
  readonly items: ReadonlyArray<Pick<ConfigItem, 'id' | 'name'>>;
  readonly selected: string[];
  readonly onToggle: (name: string) => void;
  readonly search: string;
  readonly onSearch: (v: string) => void;
  readonly placeholder: string;
  readonly emptyLabel?: string;
}) {
  return (
    <div className="border-input overflow-hidden rounded-md border">
      <SelectedChips selected={selected} onToggle={onToggle} />
      <PickerSearch value={search} placeholder={placeholder} onChange={onSearch} />
      <div className="max-h-36 overflow-y-auto">
        {items.length === 0 ? (
          <p className="text-muted-foreground py-3 text-center text-xs">{emptyLabel}</p>
        ) : (
          items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={cn(
                'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition-colors',
                selected.includes(item.name) ? 'bg-primary/10 text-primary-ink' : 'hover:bg-muted'
              )}
              onClick={() => {
                onToggle(item.name);
              }}>
              {item.name}
              {selected.includes(item.name) ? (
                <Check className="text-primary-ink size-3 shrink-0" />
              ) : null}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

/** Language picker whose search reaches the whole ISO 639-3 set, not just what was preloaded. */
// GET /api/config/languages returns only the default-visible entries — those with an ISO 639-1 code.
// The pack carries the full curated list (~7,000) so ids match Apollo's, which is a megabyte of JSON
// nobody should download to fill a picker, so searching queries the server instead of an array here.
export function LanguagePicker({
  languages,
  selected,
  onToggle
}: {
  readonly languages: ConfigItem[];
  readonly selected: string[];
  readonly onToggle: (name: string) => void;
}) {
  const [search, setSearch] = useState('');
  const {data: matches, isFetching} = useLanguageSearch(search);

  const trimmed = search.trim();
  const isSearching = trimmed.length > 0;

  // A one-character term is below the server's threshold, so narrow the preloaded set locally
  // rather than showing everything while the analyst is still typing.
  const items = isSearching
    ? trimmed.length >= 2
      ? (matches ?? [])
      : languages.filter((l) => l.name.toLowerCase().includes(trimmed.toLowerCase()))
    : languages;

  return (
    <MultiPicker
      items={items}
      selected={selected}
      search={search}
      placeholder="Search languages…"
      emptyLabel={isFetching ? 'Searching…' : 'No results'}
      onToggle={onToggle}
      onSearch={setSearch}
    />
  );
}

/** Renders the location hierarchy to whatever depth the data actually has. */
// Locations arrive from the reference-data pack as a flat list with an explicit parent_id, and the
// real hierarchy is four levels deep — UN M.49 region, sub-region, intermediate region, then ISO
// 3166-1 country. The picker derives the tree from the flat list itself, so neither call site
// repeats the grouping and neither has to know how deep it goes.
export function LocationPicker({
  locations,
  selected,
  onToggle,
  search,
  onSearch
}: {
  readonly locations: ConfigItem[];
  readonly selected: string[];
  readonly onToggle: (name: string) => void;
  readonly search: string;
  readonly onSearch: (v: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const {roots, childrenById} = useMemo(() => buildLocationTree(locations), [locations]);

  // Ancestor path, shown beside a search hit. Once countries sit under sub-regions there are names
  // that mean nothing alone — "Georgia" is a country and a US state, "Congo" appears twice in ISO
  // 3166 — so a bare match list would make an analyst guess.
  const pathById = useMemo(() => buildLocationPaths(locations), [locations]);

  const filtered: ConfigItem[] | null = useMemo(() => {
    if (!search) {
      return null;
    }

    const needle = search.toLowerCase();
    return locations.filter((l) => l.name.toLowerCase().includes(needle));
  }, [locations, search]);

  const toggleExpand = (id: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  return (
    <div className="border-input overflow-hidden rounded-md border">
      <SelectedChips selected={selected} onToggle={onToggle} />
      <PickerSearch value={search} placeholder="Search locations…" onChange={onSearch} />
      <div className="max-h-44 overflow-y-auto">
        {filtered ? (
          filtered.length === 0 ? (
            <p className="text-muted-foreground py-3 text-center text-xs">No results</p>
          ) : (
            filtered.map((loc) => (
              <LocationRow
                key={loc.id}
                loc={loc}
                selected={selected}
                depth={0}
                path={pathById.get(loc.id)}
                onToggle={onToggle}
              />
            ))
          )
        ) : (
          roots.map((root) => (
            <LocationNode
              key={root.id}
              loc={root}
              depth={0}
              childrenById={childrenById}
              selected={selected}
              expanded={expanded}
              onToggle={onToggle}
              onToggleExpand={toggleExpand}
            />
          ))
        )}
      </div>
    </div>
  );
}

/** One node and, when expanded, its subtree. */
// Recurses rather than handling a fixed two levels, so adding a level to the data changes nothing here.
function LocationNode({
  loc,
  depth,
  childrenById,
  selected,
  expanded,
  onToggle,
  onToggleExpand
}: {
  readonly loc: ConfigItem;
  readonly depth: number;
  readonly childrenById: Map<string, ConfigItem[]>;
  readonly selected: string[];
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (name: string) => void;
  readonly onToggleExpand: (id: string) => void;
}) {
  const childItems = childrenById.get(loc.id) ?? [];

  if (childItems.length === 0) {
    return <LocationRow loc={loc} selected={selected} depth={depth} onToggle={onToggle} />;
  }

  const isExpanded = expanded.has(loc.id);
  const isSelected = selected.includes(loc.name);

  // Expansion state stays with the picker (it is shared across the search and tree views), so the
  // Collapsible is controlled; `block` drops its default 8px trigger-to-panel gap, which would
  // open a seam between a parent row and its first child.
  return (
    <Collapsible
      open={isExpanded}
      className="block"
      onOpenChange={() => {
        onToggleExpand(loc.id);
      }}>
      <div className="group flex items-center" style={{paddingLeft: indentFor(depth)}}>
        <CollapsibleTrigger
          aria-label={isExpanded ? `Collapse ${loc.name}` : `Expand ${loc.name}`}
          className="text-muted-foreground hover:text-foreground hover:bg-muted -my-0.5 flex cursor-pointer items-center justify-center py-2 pr-1.5 pl-0.5">
          <ChevronRight className="size-3 transition-transform in-data-panel-open:rotate-90" />
        </CollapsibleTrigger>
        <button
          type="button"
          className={cn(
            'flex-1 px-1 py-1.5 text-left text-xs font-medium transition-colors',
            isSelected ? 'text-primary-ink' : 'hover:text-foreground text-muted-foreground'
          )}
          onClick={() => {
            onToggle(loc.name);
          }}>
          {loc.name}
          {isSelected ? <Check className="text-primary-ink ml-1 inline size-3" /> : null}
        </button>
      </div>
      <CollapsibleContent>
        {childItems.map((child) => (
          <LocationNode
            key={child.id}
            loc={child}
            depth={depth + 1}
            childrenById={childrenById}
            selected={selected}
            expanded={expanded}
            onToggle={onToggle}
            onToggleExpand={onToggleExpand}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function LocationRow({
  loc,
  selected,
  onToggle,
  depth,
  path
}: {
  readonly loc: ConfigItem;
  readonly selected: string[];
  readonly onToggle: (name: string) => void;
  readonly depth: number;
  readonly path?: string | undefined;
}) {
  const isSelected = selected.includes(loc.name);
  return (
    <button
      type="button"
      // A leaf has no chevron and no label-button padding, so it adds both to line up exactly with
      // the text of a sibling branch rather than sitting a few pixels to its left.
      style={{paddingLeft: indentFor(depth) + ChevronWidth + LabelPadding}}
      className={cn(
        'flex w-full items-center justify-between gap-2 py-1.5 pr-3 text-left text-xs transition-colors',
        isSelected ? 'bg-primary/10 text-primary-ink' : 'hover:bg-muted'
      )}
      onClick={() => {
        onToggle(loc.name);
      }}>
      <span className="min-w-0 truncate">
        {loc.name}
        {path ? <span className="text-muted-foreground ml-1.5">{path}</span> : null}
      </span>
      {isSelected ? <Check className="text-primary-ink size-3 shrink-0" /> : null}
    </button>
  );
}
